import {
  GEOMETRY_EPOCH_HEADER,
  isGeometryEpoch,
  withGeometryEpoch,
  type GeometryKey,
} from '@vitals/shared';
import type { GeometryEpochSource } from '../state/geometryEpochHub.js';

/**
 * Keeps a tab's drawn route geometry in step with the server's build.
 *
 * Drawn geometry is loaded once per view; the server announces a content
 * epoch per payload over the WS (see geometryEpochs.ts in shared). When the
 * announced epoch differs from the one this tab drew, the loader refetches
 * `?v=<schema>&e=<epoch>` — a new CDN cache key, so no edge rule change is
 * needed — and swaps the geometry in one assignment once it arrives. Until
 * then (and if the refetch fails) the old geometry stays drawn.
 */

/** Spread of the first refetch after a new epoch: every tab hears it at once after a deploy. */
export const REFETCH_JITTER_MS = 10_000;
/** First retry delay after a failed refetch; doubles per consecutive failure. */
export const RETRY_BASE_MS = 5_000;
/** Ceiling on the retry delay (before its ±50% jitter). */
export const RETRY_MAX_MS = 300_000;

export interface EpochedFetchResult<T> {
  payload: T;
  /** Epoch named by the response header, or null if absent/malformed (old server or old edge copy). */
  epoch: string | null;
}

/**
 * Fetch a geometry payload, optionally for a specific epoch, and report the
 * epoch the server says it served. Never throws: any failure is null so the
 * caller keeps whatever it already has.
 */
export async function fetchEpochedGeometry<T>(
  url: string,
  epoch: string | undefined,
): Promise<EpochedFetchResult<T> | null> {
  try {
    const res = await fetch(withGeometryEpoch(url, epoch));
    if (!res.ok) return null;
    const header = res.headers.get(GEOMETRY_EPOCH_HEADER);
    return { payload: (await res.json()) as T, epoch: isGeometryEpoch(header) ? header : null };
  } catch {
    return null;
  }
}

export interface EpochedGeometryLoader {
  /** Epoch of the geometry currently drawn (null until known). */
  epoch(): string | null;
  destroy(): void;
}

export function createEpochedGeometry<T>(opts: {
  key: GeometryKey;
  /** Schema-versioned endpoint URL (`…/api/x-geometry?v=N`). */
  url: string;
  /** Called with each successfully loaded payload; the caller swaps it in. */
  onGeometry: (payload: T) => void;
  /** Announced epochs; omitted → a plain one-shot load. */
  epochs?: GeometryEpochSource;
  /** Injectable for tests; defaults to fetchEpochedGeometry. Must not reject. */
  fetchGeometry?: (url: string, epoch: string | undefined) => Promise<EpochedFetchResult<T> | null>;
  /** Injectable for tests; defaults to Math.random. Used only for jitter. */
  rng?: () => number;
}): EpochedGeometryLoader {
  const fetchGeometry = opts.fetchGeometry ?? fetchEpochedGeometry<T>;
  const rng = opts.rng ?? Math.random;

  let drawnEpoch: string | null = null;
  let hasPayload = false;
  // Latest epoch the server announced for this key.
  let target: string | null = null;
  // Announced epoch whose fetch completed with a served epoch that is the
  // target or NEWER than it. Never chase such an announcement again — only a
  // new announcement triggers another fetch.
  let settledTarget: string | null = null;
  // Every epoch this loader has drawn or heard announced. Epochs are hashes
  // with no order, so "older" means "already known": a response naming a
  // known epoch other than the target came from an old build (a machine not
  // yet replaced mid-deploy, or an edge copy cached from one); a response
  // naming an unknown epoch came from a build newer than the announcement.
  const knownEpochs = new Set<string>();
  let inFlight = false;
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;

  const needsFetch = (): boolean =>
    target !== null && target !== drawnEpoch && target !== settledTarget;

  const delayMs = (): number =>
    failures === 0
      ? rng() * REFETCH_JITTER_MS
      : Math.min(RETRY_BASE_MS * 2 ** (failures - 1), RETRY_MAX_MS) * (0.5 + rng());

  const reconcile = (): void => {
    if (destroyed || inFlight || timer !== null || !needsFetch()) return;
    timer = setTimeout(() => {
      timer = null;
      if (needsFetch()) void load(target ?? undefined);
    }, delayMs());
  };

  const load = async (epoch: string | undefined): Promise<void> => {
    inFlight = true;
    const result = await fetchGeometry(opts.url, epoch);
    inFlight = false;
    if (destroyed) return;
    if (result === null) {
      // Only an announced epoch is worth retrying; a failed plain load waits
      // for the server to announce one (e.g. LIRR 503 until it has loaded).
      if (epoch !== undefined) failures += 1;
    } else if (isStaleResponse(epoch, result.epoch)) {
      // Older than requested: the target stays pending and is retried under
      // the backoff. Draw it only if there is nothing drawn at all.
      failures += 1;
      if (!hasPayload) draw(result.payload, result.epoch);
    } else {
      failures = 0;
      if (epoch !== undefined) settledTarget = epoch;
      draw(result.payload, result.epoch ?? epoch ?? null);
    }
    reconcile();
  };

  const isStaleResponse = (requested: string | undefined, served: string | null): boolean =>
    requested !== undefined && served !== null && served !== requested && knownEpochs.has(served);

  const draw = (payload: T, epoch: string | null): void => {
    drawnEpoch = epoch;
    hasPayload = true;
    if (epoch !== null) knownEpochs.add(epoch);
    opts.onGeometry(payload);
  };

  const announce = (epoch: string | undefined): void => {
    if (!isGeometryEpoch(epoch)) return;
    knownEpochs.add(epoch);
    target = epoch;
    reconcile();
  };

  const initial = opts.epochs?.get(opts.key);
  if (isGeometryEpoch(initial)) {
    knownEpochs.add(initial);
    target = initial;
  }
  const offEpochs = opts.epochs?.onChange((epochs) => announce(epochs[opts.key]));
  void load(target ?? undefined);

  return {
    epoch: () => drawnEpoch,
    destroy: () => {
      destroyed = true;
      offEpochs?.();
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
}
